import {
  DocketDisplayItemTypeHeight,
  CONTINUE,
} from '../../../common/constants';
import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayOrderItem,
} from '../../../common/types';
import { PaginatedDocketItems, isOverflowingDocket } from '../../layoutHelper';
import { headerOrder } from '../../../common/constants';

export const getSingleRowPaginatedDocketItems = (
  items: DocketDisplayItem[],
  scale: (n: number) => number,
  columnHeight: number,
  colsPerPage: number,
): PaginatedDocketItems[] => {
  // Pre-calculations and constants
  const continueHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
  );
  const continuedHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
  );

  // Filter items once at the beginning
  const filteredItems = items.filter(
    item =>
      item.type !== DocketDisplayItemType.CONTINUE &&
      item.type !== DocketDisplayItemType.CONTINUED,
  );

  // Precompute the expected total pages estimate for reuse
  const totalPagesEstimate = Math.ceil(
    filteredItems.length / (colsPerPage * 5),
  );

  // Initialize page structure with correct type structure
  let currentPage = 1;
  let currentColumn = 1;
  let currentHeight = 0;
  let currentColumnObject = {
    column: currentColumn,
    rows: [{ row: 1, items: [] as DocketDisplayItem[] }], // Fixed: added rows structure
  };
  const pages: PaginatedDocketItems[] = [
    { page: currentPage, columns: [currentColumnObject] },
  ];

  // Tracking variables
  let previousDocketId: string | undefined;
  let previousRowPosition: number | undefined;

  // Process items in a single pass
  filteredItems.forEach((item, index) => {
    // Extract docket information
    let docketId: string | undefined;
    let rowPosition = 1;

    if (item.type === DocketDisplayItemType.DOCKET_HEADER) {
      docketId = (item as DocketDisplayOrderItem).id;
      rowPosition = item?.position?.rowPosition || 1;
      previousDocketId = docketId;
      previousRowPosition = rowPosition;
    } else if (previousDocketId) {
      docketId = previousDocketId;
      rowPosition = previousRowPosition || 1;
    }

    // Check if we need a new column
    const heightWithContinue =
      currentHeight + scale(item.height) + continueHeight;
    const needNewColumn =
      isOverflowingDocket(
        filteredItems,
        index,
        columnHeight,
        currentHeight,
        continueHeight,
        scale,
      ) ||
      heightWithContinue > columnHeight ||
      (index > 0 &&
        headerOrder.some((headerType, priorityIndex) => {
          if (item.type === headerType) {
            // If this is highest priority header, always split
            if (priorityIndex === 0) return true;

            // For lower priority headers, only split if no higher priority header precedes it
            const previousItemType = filteredItems[index - 1]?.type;
            return !headerOrder
              .slice(0, priorityIndex)
              .includes(previousItemType);
          }
          return false;
        }));

    // Stop CONTINUE/CONTINUED logic for header items
    const isHeaderItem = headerOrder.includes(item.type);

    if (needNewColumn) {
      // Only add CONTINUE tag if not a header item and we have a docket ID
      if (!isHeaderItem && docketId) {
        // Add CONTINUE tag to current column (to the first row's items)
        currentColumnObject.rows[0].items.push({
          type: DocketDisplayItemType.CONTINUE,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
          docketId,
          position: {
            parentRowPosition: rowPosition,
            pageIndicator: `${currentPage}/${totalPagesEstimate}`,
          },
        });
      }

      // Create new column or page
      if (currentColumn + 1 > colsPerPage) {
        // New page
        currentPage++;
        currentColumn = 1;
        currentColumnObject = {
          column: currentColumn,
          rows: [{ row: 1, items: [] }], // Fixed: proper rows structure
        };
        pages.push({ page: currentPage, columns: [currentColumnObject] });
      } else {
        // New column on same page
        currentColumn++;
        currentColumnObject = {
          column: currentColumn,
          rows: [{ row: 1, items: [] }], // Fixed: proper rows structure
        };
        pages[currentPage - 1].columns.push(currentColumnObject);
      }

      // Only add CONTINUED tag if not a header item and we have a docket ID
      if (!isHeaderItem && docketId) {
        // Add CONTINUED tag to new column (to the first row's items)
        currentColumnObject.rows[0].items.push({
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
          docketId,
          position: {
            parentRowPosition: rowPosition,
            pageIndicator: `${currentPage}/${totalPagesEstimate}`,
          },
        });
        currentHeight = continuedHeight;
      } else {
        currentHeight = 0;
      }
    } else if (currentColumn > colsPerPage) {
      // New page if columns exceed limit
      currentPage++;
      currentColumn = 1;
      currentColumnObject = {
        column: currentColumn,
        rows: [{ row: 1, items: [] }], // Fixed: proper rows structure
      };
      pages.push({ page: currentPage, columns: [currentColumnObject] });

      // Add CONTINUED tag if applicable - but not for header items
      if (!isHeaderItem && docketId) {
        currentColumnObject.rows[0].items.push({
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
          docketId,
          position: {
            parentRowPosition: rowPosition,
            pageIndicator: `${currentPage}/${totalPagesEstimate}`,
          },
        });
        currentHeight = continuedHeight;
      } else {
        currentHeight = 0;
      }
    }

    // Add the current item (to the first row's items)
    currentColumnObject.rows[0].items.push(item);
    currentHeight += scale(item.height);
  });

  const docketTotalHeights = new Map<string, number>();
  const docketItems = new Map<string, DocketDisplayItem[]>();

  filteredItems.forEach(item => {
    if ((item as DocketDisplayOrderItem).docketId) {
      const docketId = (item as DocketDisplayOrderItem).docketId;
      // Update height
      docketTotalHeights.set(
        docketId,
        (docketTotalHeights.get(docketId) || 0) + scale(item.height),
      );

      // Update items collection
      const items = docketItems.get(docketId) || [];
      items.push(item);
      docketItems.set(docketId, items);
    } else if (item.type === DocketDisplayItemType.DOCKET_HEADER) {
      const docketId = (item as DocketDisplayOrderItem).id;
      if (docketId) {
        docketTotalHeights.set(docketId, scale(item.height));
        docketItems.set(docketId, [item]);
      }
    }
  });

  // Calculate expected chunks based on height
  const docketExpectedChunks = new Map<string, number>();
  docketTotalHeights.forEach((height, docketId) => {
    const continueLabelsPerChunk = continueHeight + continuedHeight;
    const effectiveColHeight = columnHeight - continueLabelsPerChunk;
    const chunksNeeded = Math.max(1, Math.ceil(height / effectiveColHeight));
    docketExpectedChunks.set(docketId, chunksNeeded);
  });

  // Track current chunk for each docket
  const docketCurrentChunk = new Map<string, number>();

  pages.forEach(page => {
    page.columns.forEach(column => {
      column.rows.forEach(row => {
        // Fixed: iterate through rows
        row.items.forEach((item: DocketDisplayItem) => {
          if (
            ((item as DocketDisplayOrderItem).type ===
              DocketDisplayItemType.CONTINUE ||
              (item as DocketDisplayOrderItem).type ===
                DocketDisplayItemType.CONTINUED) &&
            'docketId' in item &&
            item.docketId
          ) {
            const docketId = (item as DocketDisplayOrderItem).docketId;
            const totalChunks = docketExpectedChunks.get(docketId) || 1;

            const docketItem = item as DocketDisplayOrderItem;
            const isContinue =
              docketItem.type === DocketDisplayItemType.CONTINUE;

            // Get current chunk
            const currentChunk = docketCurrentChunk.get(docketId) || 1;

            // Ensure position exists
            docketItem.position = docketItem.position || {};
            docketItem.position.pageIndicator = `${currentChunk}/${totalChunks}`;

            docketCurrentChunk.set(
              docketId,
              isContinue ? currentChunk + 1 : currentChunk,
            );
          }
        });
      });
    });
  });

  return pages;
};
